import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton } from '@material-ui/core';
import GridContainer from '../../../components/Grid/GridContainer';
import { IndeterminateCheckBoxIcon } from '../../../components/Icons/Icon';
import GridItem from '../../../components/Grid/GridItem';
import Button from '../../../components/Button/CustomButton';
import Typography from '../../../components/Typography/CustomTypography';
import Snackbar from '../../../components/Snackbar/Snackbar';
import AlertDialog from '../../../components/Dialog/AlertDialog';
import Pagination from '../../../components/Pagination/Pagination';
import CleanableInput from '../../../components/Input/CleanableInput';
import UploadButton from '../../../components/Button/UploadButton';
import Loader from '../../../components/Loader/Loader';
import Validation from '../../../common/Validation';

import styles from '../../../assets/jss/pages/advertisementManagementStyle';

const useStyles = makeStyles(styles);

const AdvertisementItems = (props) => {
    const {
        index,
        advDataItem,
        handleShowConfirmDialog,
        handleUpdateAdvertisement,
        handleAddAdvertisement,
        arrData,
        setAdsArr,
        indexOfPage,
        setIndexOfPage,
        setIsNewAdd,
        eventInfo,
    } = props;
    const classes = useStyles();
    const [params, setParams] = useState({});
    const [isChangeImage, setIsChangeImage] = useState(false);
    const [validateResults, setValidateResults] = useState({});
    const intl = useIntl();

    useEffect(() => {
        if (!_.isEmpty(advDataItem)) {
            if (!_.isEmpty(eventInfo.languages)) {
                setParams({
                    id: advDataItem.id,
                    image: advDataItem.image,
                    description: advDataItem.description,
                    description_en: advDataItem.description_en,
                });
            } else {
                setParams({
                    id: advDataItem.id,
                    image: advDataItem.image,
                    description: advDataItem.description,
                });
            }
        }
    }, [advDataItem]);

    useEffect(() => {
        arrData[indexOfPage - index] = { ...params };
    }, [params]);

    const handleCreateUpdate = () => {
        if (validate()) {
            if (params.id) {
                handleUpdateAdvertisement(createDataToCallApi());
            } else {
                handleAddAdvertisement(createDataToCallApi());
            }
        }
    };

    const createDataToCallApi = () => {
        let dataToCallApi = {
            id: params.id,
            imageData: (isChangeImage && params.imageData) || '',
            description: params.description,
        };
        if (!_.isEmpty(eventInfo.languages)) {
            dataToCallApi = {
                id: params.id,
                imageData: (isChangeImage && params.imageData) || '',
                description: params.description,
                description_en: params.description_en,
            };
        }
        return dataToCallApi;
    };

    const handleSetParams = useCallback((field, valueChange) => {
        setParams((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const handleUploadImage = (file, urlImage) => {
        handleSetParams('imageData', file);
        handleSetParams('image', urlImage);
        setIsChangeImage(true);
    };

    const deleteAdsForm = () => {
        if (params.id) {
            handleShowConfirmDialog(params.id);
        } else {
            arrData.splice(arrData.lenght - index, 1);
            setAdsArr([...arrData]);
            setIndexOfPage(indexOfPage - 1);
            setIsNewAdd(true);
        }
    };

    const validate = () => {
        const {
            description, image, description_en,
        } = params;

        const validateRules = {
            description: [() => Validation.isNotEmpty(description),
            ],
            image: [() => Validation.isNotEmpty(image),
            ],
        };
        if (!_.isEmpty(eventInfo.languages)) {
            validateRules.description_en = [
                () => Validation.isNotEmpty(description_en),
                () => Validation.validateLatin(description_en),
            ];
        }
        const validateResult = Validation.validateAllRules(validateRules);
        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    return (
        <GridContainer>
            <GridItem className={classes.main}>
                <fieldset className={classes.fieldset}>
                    <legend>
                        <Typography variant="h5" color="primary" bold>{`公告 ${index}`}</Typography>
                    </legend>
                    <IconButton size="small" classes={{ root: classes.advertisementRemoveButton }} onClick={deleteAdsForm}>
                        <IndeterminateCheckBoxIcon size="40px" />
                    </IconButton>
                    <GridContainer style={{ margin: '15px 0' }}>
                        <GridItem md={2}>
                            <Typography required bold>
                                公告画像
                            </Typography>
                        </GridItem>
                        <GridItem md={10}>
                            <UploadButton
                                text="Upload an Image"
                                type="image"
                                onChange={handleUploadImage}
                                imagePreview={params.image}
                            />
                            {
                                validateResults.image && validateResults.image.result !== 'success' && (
                                    <Typography color="danger" style={{ fontSize: '0.75rem', marginLeft: '15px', fontWeight: '400' }}>
                                        {validateResults.image
                                            ? intl.formatMessage({ id: validateResults.image.message }) : ''}
                                    </Typography>
                                )
                            }
                        </GridItem>
                    </GridContainer>
                    <GridContainer style={{ margin: '15px 0' }}>
                        <GridItem md={2}>
                            <Typography required bold>
                                {!_.isEmpty(eventInfo.languages) ? '内容（和文）' : '内容'}
                            </Typography>
                        </GridItem>
                        <GridItem md={10}>
                            <CleanableInput
                                isMultiline
                                defaultValue={params.description}
                                onChange={(value) => handleSetParams('description', value)}
                                error={!!validateResults.description}
                                helperText={validateResults.description
                                    ? intl.formatMessage({ id: validateResults.description.message }) : ''}
                            />
                        </GridItem>
                    </GridContainer>
                    {!_.isEmpty(eventInfo.languages) && (
                        <GridContainer style={{ margin: '15px 0' }}>
                            <GridItem md={2}>
                                <Typography required bold>
                                    内容（英文）
                                </Typography>
                            </GridItem>
                            <GridItem md={10}>
                                <CleanableInput
                                    isMultiline
                                    defaultValue={params.description_en}
                                    onChange={(value) => handleSetParams('description_en', value)}
                                    error={!!validateResults.description_en}
                                    helperText={validateResults.description_en
                                        ? intl.formatMessage({ id: validateResults.description_en.message }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                    <GridItem className={classes.button}>
                        <Button
                            size="md"
                            color="primary"
                            onClick={handleCreateUpdate}
                        >
                            <FormattedMessage id="apply" />
                        </Button>
                    </GridItem>
                </fieldset>
            </GridItem>
        </GridContainer>
    );
};

AdvertisementItems.defaultProps = {
    index: 1,
    advDataItem: [],
    arrData: [],
    indexOfPage: 20,
    eventInfo: {},
};

AdvertisementItems.propTypes = {
    index: PropTypes.number,
    advDataItem: PropTypes.oneOfType([() => null, PropTypes.objectOf]),
    handleShowConfirmDialog: PropTypes.func.isRequired,
    handleUpdateAdvertisement: PropTypes.func.isRequired,
    handleAddAdvertisement: PropTypes.func.isRequired,
    arrData: PropTypes.arrayOf(PropTypes.object),
    setAdsArr: PropTypes.func.isRequired,
    setIndexOfPage: PropTypes.func.isRequired,
    setIsNewAdd: PropTypes.func.isRequired,
    indexOfPage: PropTypes.number,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.objectOf]),
};

const AdvertisementManagement = (props) => {
    const {
        updateSuccess,
        deleteSuccess,
        createSuccess,
        fetchGetAds,
        fetchCreateAds,
        fetchUpdateAds,
        fetchDeleteAds,
        fetchClearGetAds,
        fetchClearCreateAds,
        fetchClearUpdateAds,
        fetchClearDeleteAds,
        adsData,
        advertisementManagent,
        isLoading,
        errorsDelete,
        errorsUpdate,
        eventInfo,
    } = props;

    const urlParams = useParams();
    const [adsArr, setAdsArr] = useState([]);
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const [isNewAdd, setIsNewAdd] = useState(true);
    const [snackbar, setSnackbar] = useState({ isOpen: false, type: 'success', message: '' });
    const [idDelete, setIdDelete] = useState(false);
    const [indexOfPage, setIndexOfPage] = useState(0);

    useEffect(() => {
        fetchGetAds({
            event_name: urlParams.shortName,
            company_id: urlParams.idCompany,
        });
        return () => {
            setAdsArr([]);
            fetchClearUpdateAds();
            fetchClearGetAds();
            fetchClearCreateAds();
            fetchClearDeleteAds();
        };
    }, []);

    useEffect(() => {
        if (!_.isEmpty(adsData)) {
            setAdsArr(adsData);
            setIndexOfPage(advertisementManagent.total_length - 20 * (advertisementManagent.page - 1));
        }
    }, [adsData]);

    useEffect(() => {
        if (updateSuccess) {
            setSnackbar({
                isOpen: true,
                type: 'success',
                message: <FormattedMessage id="apiSuccess.default" />,
            });
        }
    }, [updateSuccess]);

    useEffect(() => {
        if (deleteSuccess) {
            handleCloseConfirmDialog();
            setIndexOfPage(indexOfPage - 1);
            fetchGetAds({
                event_name: urlParams.shortName,
                company_id: urlParams.idCompany,
            });
            setIsNewAdd(true);
            setSnackbar({
                isOpen: true,
                type: 'success',
                message: <FormattedMessage id="apiSuccess.default" />,
            });
        }
    }, [deleteSuccess]);

    useEffect(() => {
        if (createSuccess) {
            fetchGetAds({
                event_name: urlParams.shortName,
                company_id: urlParams.idCompany,
            });
            setIsNewAdd(true);
            setSnackbar({
                isOpen: true,
                type: 'success',
                message: <FormattedMessage id="apiSuccess.default" />,
            });
        }
    }, [createSuccess]);

    useEffect(() => {
        if (!_.isEmpty(errorsUpdate) || !_.isEmpty(errorsDelete)) {
            handleCloseConfirmDialog();
            setSnackbar({
                isOpen: true,
                type: 'error',
                message: errorsUpdate?.message?.message || errorsDelete?.message?.message,
            });
            fetchClearUpdateAds();
            fetchClearGetAds();
            fetchClearDeleteAds();
            fetchGetAds({
                event_name: urlParams.shortName,
                company_id: urlParams.idCompany,
            });
        }
    }, [errorsDelete, errorsUpdate]);

    const handleDeleteAdvertisement = () => {
        const filterAds = adsArr.filter((ads) => ads.id !== idDelete);
        setAdsArr(filterAds);
        fetchDeleteAds({
            event_name: urlParams.shortName,
            ads_id: idDelete,
        });
    };

    const handleUpdateAdvertisement = (adv) => {
        fetchUpdateAds({
            event_name: urlParams.shortName,
            image: adv.imageData,
            ads_id: adv.id,
            description: adv.description,
            ...adv?.description_en && { description_en: adv.description_en },
        });
    };

    const handleAddAdvertisement = (adv) => {
        fetchCreateAds({
            event_name: urlParams.shortName,
            image: adv.imageData,
            description: adv.description,
            ...adv?.description_en && { description_en: adv.description_en },
        });
    };

    const handleSearchWithPage = (pageChange) => {
        fetchGetAds({
            event_name: urlParams.shortName,
            company_id: urlParams.idCompany,
            page: pageChange,
        });
    };

    const handleShowConfirmDialog = (adsId) => {
        if (!isNewAdd) {
            const conf = window.confirm('保存されてない変更がありますが、破棄して現在のアクションを行ってよろしいですか？');
            if (!conf) {
                return;
            }
        }
        setIsOpenConfirmDialog(true);
        setIdDelete(adsId);
    };
    const handleCloseConfirmDialog = () => {
        setIsOpenConfirmDialog(false);
    };
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, isOpen: false });
    };

    return (
        <>
            <GridContainer style={{ justifyContent: 'center' }}>
                <GridItem style={{ width: '80%' }}>
                    {_.isEmpty(adsData) && (
                        <div style={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '20px',
                        }}
                        >
                            この出展企業は広告を登録していません。
                        </div>
                    )}

                    {adsArr && adsArr.map((adv, index) => (
                        <AdvertisementItems
                            key={adv.id}
                            index={indexOfPage - index}
                            advDataItem={adv}
                            handleShowConfirmDialog={handleShowConfirmDialog}
                            handleUpdateAdvertisement={handleUpdateAdvertisement}
                            handleAddAdvertisement={handleAddAdvertisement}
                            arrData={adsArr}
                            setAdsArr={setAdsArr}
                            indexOfPage={indexOfPage}
                            setIndexOfPage={setIndexOfPage}
                            setIsNewAdd={setIsNewAdd}
                            eventInfo={eventInfo}
                        />
                    ))}
                </GridItem>
                <GridItem>
                    {!_.isEmpty(adsData) && (
                        <Pagination
                            page={advertisementManagent.page}
                            total={advertisementManagent.total_page}
                            onChange={(pageChange) => handleSearchWithPage(pageChange)}
                        />
                    )}
                </GridItem>
            </GridContainer>

            <Snackbar
                open={snackbar.isOpen}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
                severity={snackbar.type}
            />
            <AlertDialog
                title={<FormattedMessage id="dialog.confirmDeleteTitle" />}
                handleOpen={isOpenConfirmDialog}
                handleClose={handleCloseConfirmDialog}
                handleSubmit={handleDeleteAdvertisement}
                contents="削除しますか？"

            />
            <Loader isLoading={isLoading} />
        </>
    );
};

AdvertisementManagement.defaultProps = {
    adsData: {},
    updateSuccess: false,
    deleteSuccess: false,
    createSuccess: false,
    advertisementManagent: {},
    errorsDelete: {},
    errorsUpdate: {},
    eventInfo: {},
};

AdvertisementManagement.propTypes = {
    fetchGetAds: PropTypes.func.isRequired,
    fetchCreateAds: PropTypes.func.isRequired,
    fetchUpdateAds: PropTypes.func.isRequired,
    fetchDeleteAds: PropTypes.func.isRequired,
    fetchClearGetAds: PropTypes.func.isRequired,
    fetchClearCreateAds: PropTypes.func.isRequired,
    fetchClearUpdateAds: PropTypes.func.isRequired,
    fetchClearDeleteAds: PropTypes.func.isRequired,
    adsData: PropTypes.oneOfType([() => null, PropTypes.objectOf]),
    updateSuccess: PropTypes.bool,
    deleteSuccess: PropTypes.bool,
    createSuccess: PropTypes.bool,
    advertisementManagent: PropTypes.oneOfType([() => null, PropTypes.object]),
    isLoading: PropTypes.bool.isRequired,
    errorsDelete: PropTypes.oneOfType([() => null, PropTypes.object]),
    errorsUpdate: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
};

export default AdvertisementManagement;
